import React, { createContext, useContext, useState, useCallback } from "react";
import api from "../helpers/api";
import { UserEvent, TrackedUrl } from "../models/UserEvent";

interface UserEventContextType {
  userEvents: UserEvent[];
  fetchUrls: () => Promise<void>;
  deleteEvent: (eventId: number) => Promise<void>;
  updateUrl: (url: string, data: Partial<TrackedUrl>) => Promise<void>;
  loading: boolean;
}

const UserEventContext = createContext<UserEventContextType | null>(null);

export const UserEventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [urls, setUrls] = useState<UserEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUrls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/user/events");
      setUrls(response.data.sort((a: UserEvent, b: UserEvent) => a.id - b.id));
    } catch (error) {
      console.error("Failed to fetch tracked URLs");
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEvent = async (eventId: number) => {
    try {
      await api.delete("/user/events", { data: { eventId } });
    } catch (error) {
      console.log("Failed to delete event");
    }
    await fetchUrls();
  };

  const updateUrl = async (url: string, data: Partial<TrackedUrl>) => {
    try {
      await api.put("/user/trackedurls", { url, ...data });
    } catch (error) {
      console.log("Failed to update event");
    }
    await fetchUrls();
  };

  return (
    <UserEventContext.Provider
      value={{ userEvents: urls, fetchUrls, deleteEvent, updateUrl, loading }}
    >
      {children}
    </UserEventContext.Provider>
  );
};

export const useUserEvents = () => {
  const context = useContext(UserEventContext);
  if (!context)
    throw new Error("useUserEvents must be used within UserEventProvider");
  return context;
};
